import { HTMLProps, MouseEventHandler } from 'react';
import NextLink from 'next/link';
import { Reference } from '../../../../types/reference';
import { resolveReferenceTarget, resolveReferenceProps } from '../../../../helpers/reference';
import { usePageLoading } from '@frontastic/catwalk/js/helper/hooks/usePageLoading';
import { usePathname, useSearchParams } from 'next/navigation';

type Props = Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref' | 'key'> & {
  reference: Reference;
};

const ReferenceComponent: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  reference,
  onClick,
  children,
  ...anchorProps
}) => {
  const { dispatchPageLoadingStart } = usePageLoading();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (reference === null) {
    return <>{children}</>;
  }

  const onClickWrapper: MouseEventHandler<HTMLAnchorElement> = (event): ReturnType<typeof onClick> => {
    const resolvedRef = resolveReferenceTarget(reference);
    const searchParamsString = searchParams.toString();
    const isSamePath = resolvedRef === pathname + (searchParamsString ? '?' + searchParams.toString() : '');
    const modKeyPressed = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;

    if (reference?.openInNewWindow !== true && !isSamePath && !modKeyPressed) {
      dispatchPageLoadingStart();
    }

    if (typeof onClick === 'function') {
      return onClick(event);
    }
  };

  return (
    <NextLink
      href={resolveReferenceTarget(reference)}
      onClick={onClickWrapper}
      {...anchorProps}
      {...resolveReferenceProps(reference)}
    >
      {children}
    </NextLink>
  );
};

export default ReferenceComponent;
